import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Dropdown } from "primereact/dropdown";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import Tooltip from "@mui/material/Tooltip";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CustomAxios from "../../utils/CustomAxios";
import { useSelector } from "react-redux";
import { useGetInventory } from "../globalFunctions";
import { useNavigate } from "react-router-dom";

export function Suppliers() {
  const toast = useRef(null);
  const navigate = useNavigate();
  const inventory = useSelector(state => state.inventory.inventory);
  const getInventory = useGetInventory();
  const [suppliers, setSuppliers] = useState([]);
  const [suppliersLoading, setSuppliersLoading] = useState(false);
  const [selectedInventoryId, setSelectedInventoryId] = useState(null);
  const user_role = localStorage.getItem("role");
  const isMerchant = user_role === 'merchant';
  const hubId = localStorage.getItem("selected_hub");


  const fetchSuppliers = async () => {
    try {
      setSuppliersLoading(true);
      // For merchants, filter by their selected hub
      const inventoryId = isMerchant && hubId ? hubId : selectedInventoryId;
      const url = inventoryId ? `suppliers/?inventory_id=${inventoryId}` : "suppliers/";
      const response = await CustomAxios.get(url);
      setSuppliers(response.data || []);
    } catch (error) {
      console.error("Error fetching suppliers:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch suppliers",
        life: 3000,
      });
    } finally {
      setSuppliersLoading(false);
    }
  };


  const handleEditSupplier = (supplier) => {
    navigate(`/home/edit_supplier/${supplier.id}`);
  };

  const accept = async (id) => {
    try {
      setSuppliersLoading(true);
      await CustomAxios.delete(`suppliers/${id}/`);
      toast.current?.show({
        severity: "success",
        summary: "Deleted",
        detail: "Supplier deleted successfully",
        life: 3000,
      });
      fetchSuppliers();
    } catch (error) {
      console.error("Error deleting supplier:", error);
      
      let errorMessage = "Failed to delete supplier";
      if (error?.response?.data) {
        const errorData = error.response.data;
        if (errorData.details) {
          errorMessage = typeof errorData.details === 'string' ? errorData.details : JSON.stringify(errorData.details);
        } else if (errorData.error) {
          errorMessage = typeof errorData.error === 'string' ? errorData.error : JSON.stringify(errorData.error);
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: errorMessage,
        life: 5000,
      });
    } finally {
      setSuppliersLoading(false);
    }
  };

  const confirmDelete = (supplier) => {
    confirmDialog({
      message: (
        <Box sx={{ display: "flex", gap: "10px", flexDirection: "column" }}>
          <Box sx={{ display: "flex", gap: "20px", alignItems: "center" }}>
            <Box>
              <Typography>Supplier: {supplier.name}</Typography>
            </Box>
          </Box>
          <Typography sx={{ fontSize: "16px", fontWeight: 500 }}>
            Do you want to delete this Supplier?
          </Typography>
        </Box>
      ),
      header: 'Delete Confirmation',
      defaultFocus: 'reject',
      acceptClassName: 'p-button-danger',
      accept: () => accept(supplier.id),
    });
  };

  useEffect(() => {
    getInventory();
  }, []);

  useEffect(() => {
    fetchSuppliers();
  }, [selectedInventoryId, hubId]);

  if (!isMerchant) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "20px",
          padding: "20px",
        }}
      >
        <Typography variant="h6" color="error">
          Access Denied
        </Typography>
        <Typography>
          You don't have permission to manage suppliers.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        width: "100%",
        padding: "30px",
      }}
    >
      <Toast ref={toast} />
      <ConfirmDialog />
      <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <Typography
          sx={{ fontSize: "16px", fontWeight: "700", lineHeight: "24px" }}
        >
          Supplier Management
        </Typography>
        <Typography
          sx={{ fontSize: "14px", fontWeight: "500", lineHeight: "22px" }}
        >
          Manage your wholesale suppliers and their details.
        </Typography>
      </Box>
      <Box
        sx={{ display: "flex", justifyContent: "space-between", width: "100%", mt: 1, alignItems: "center" }}
      >
        {!isMerchant && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography sx={{ fontSize: "14px", fontWeight: "400" }}>
              Filter by Inventory:
            </Typography>
            <Dropdown
              value={selectedInventoryId}
              options={[
                { name: "All Inventories", value: null },
                ...(inventory || []).map((inv) => ({
                  name: inv.name || inv.place,
                  value: inv.value || inv.id,
                })),
              ]}
              optionLabel="name"
              optionValue="value"
              placeholder="Select Inventory"
              className="w-full md:w-20rem"
              appendTo="self"
              onChange={(e) => setSelectedInventoryId(e.value)}
              style={{ width: "250px" }}
            />
          </Box>
        )}
        <Button
          variant="contained"
          onClick={() => navigate("/home/add_supplier")}
          sx={{ textTransform: "none" }}
        >
          Add Supplier
        </Button>
      </Box>
      <Box sx={{ mt: 2 }}>
        <TableContainer sx={{ backgroundColor: "white" }}>
          <Table stickyHeader aria-label="suppliers table">
            <TableHead style={{ backgroundColor: "background.upload" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: "600", textAlign: "center" }}>
                  S No
                </TableCell>
                <TableCell sx={{ fontWeight: "600", textAlign: "left" }}>
                  Supplier Code
                </TableCell>
                <TableCell sx={{ fontWeight: "600", textAlign: "left" }}>
                  Name
                </TableCell>
                <TableCell sx={{ fontWeight: "600", textAlign: "left" }}>
                  Contact Person
                </TableCell>
                <TableCell sx={{ fontWeight: "600", textAlign: "left" }}>
                  Email
                </TableCell>
                <TableCell sx={{ fontWeight: "600", textAlign: "left" }}>
                  Phone
                </TableCell>
                <TableCell sx={{ fontWeight: "600", textAlign: "left" }}>
                  Type
                </TableCell>
                <TableCell sx={{ fontWeight: "600", textAlign: "left" }}>
                  City
                </TableCell>
                <TableCell sx={{ fontWeight: "600", textAlign: "left" }}>
                  Lead Time (Days)
                </TableCell>
                <TableCell sx={{ fontWeight: "600", textAlign: "left" }}>
                  Inventory
                </TableCell>
                <TableCell sx={{ fontWeight: "600", textAlign: "left" }}>
                  Active
                </TableCell>
                <TableCell sx={{ fontWeight: "600", textAlign: "left" }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(suppliersLoading
                ? []
                : suppliers
              ).map((supplier, index) => {
                return (
                  <TableRow key={supplier.id || index} sx={{ background: "white" }}>
                    <TableCell sx={{ textAlign: "center" }}>
                      {index + 1}
                    </TableCell>
                    <TableCell sx={{ textAlign: "left" }}>
                      {supplier.supplier_code || "-"}
                    </TableCell>
                    <TableCell sx={{ textAlign: "left" }}>
                      {supplier.name}
                    </TableCell>
                    <TableCell sx={{ textAlign: "left" }}>
                      {supplier.contact_person}
                    </TableCell>
                    <TableCell sx={{ textAlign: "left" }}>
                      {supplier.contact_email}
                    </TableCell>
                    <TableCell sx={{ textAlign: "left" }}>
                      {supplier.contact_phone}
                    </TableCell>
                    <TableCell sx={{ textAlign: "left" }}>
                      {supplier.supplier_type_display || supplier.supplier_type}
                    </TableCell>
                    <TableCell sx={{ textAlign: "left" }}>
                      {supplier.city}
                    </TableCell>
                    <TableCell sx={{ textAlign: "left" }}>
                      {supplier.lead_time_days || "-"}
                    </TableCell>
                    <TableCell sx={{ textAlign: "left" }}>
                      {supplier.inventory_name || supplier.inventory || "-"}
                    </TableCell>
                    <TableCell sx={{ textAlign: "left" }}>
                      {supplier.is_active ? "Yes" : "No"}
                    </TableCell>
                    <TableCell sx={{ textAlign: "left" }}>
                      <Tooltip title="Edit" arrow>
                        <IconButton onClick={() => handleEditSupplier(supplier)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete" arrow>
                        <IconButton
                          onClick={() => confirmDelete(supplier)}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!suppliersLoading && suppliers?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={12} sx={{ textAlign: "center" }}>
                    No suppliers found.
                  </TableCell>
                </TableRow>
              )}
              {suppliersLoading && (
                <TableRow>
                  <TableCell colSpan={12} sx={{ textAlign: "center" }}>
                    Loading...
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
}

